/**
 * Endpoints de biometría facial de EDSuite API.
 *
 * El navegador calcula el descriptor de la cara; aquí sólo se registra o se consulta ese vector
 * (nunca la foto). El registro de la cara de otra persona requiere permisos de Usuarios.
 */

import { apiClient } from '@/lib/http/api-client';

import type { BiometriaEstado } from './types';

export interface RegistrarBiometriaPayload {
  /** Una o varias capturas de la misma cara (mejora el reconocimiento con gafas o con otra luz). */
  descriptores: number[][];
  modelo: string;
  etiqueta?: string;
  /** Sólo administradores: registrar la cara de otra persona (alta de personal). */
  usuario_id?: number;
  /** Confianza media de las capturas (informativo). */
  calidad?: number;
}

export interface RespuestaRegistroBiometria extends BiometriaEstado {
  registradas: number;
  mensaje: string;
}

export const biometriaApi = {
  /** Estado de la propia biometría; con `usuarioId` (y permisos) la de otra persona. */
  estado: (usuarioId?: number, signal?: AbortSignal): Promise<BiometriaEstado> =>
    apiClient.get<BiometriaEstado>('/api/auth/biometria', {
      query: usuarioId ? { usuario_id: usuarioId } : undefined,
      signal,
      retryOnAuthError: false,
    }),

  registrar: (payload: RegistrarBiometriaPayload): Promise<RespuestaRegistroBiometria> =>
    apiClient.post<RespuestaRegistroBiometria>('/api/auth/biometria', {
      body: payload,
      retryOnAuthError: false,
    }),

  /** Borra todas las muestras de un usuario (también sirve al propio usuario). */
  borrarDeUsuario: (usuarioId: number): Promise<BiometriaEstado & { borradas: number }> =>
    apiClient.delete<BiometriaEstado & { borradas: number }>(`/api/auth/biometria/usuario/${usuarioId}`, {
      retryOnAuthError: false,
    }),

  borrar: (muestraId: number): Promise<BiometriaEstado & { borradas: number }> =>
    apiClient.delete<BiometriaEstado & { borradas: number }>(`/api/auth/biometria/${muestraId}`, {
      retryOnAuthError: false,
    }),
} as const;
